import { FC, useMemo, useState, useCallback, useEffect } from "react"
import { useAppSelector } from "../../hooks"
import { selectUserDetails } from "../../../redux/slices/auth"
import {
  useFindRateflowsQuery,
  useUpdateOrCreateRateflowMutation
} from "../../../redux/queries"
import { useFetchLoanOfficersQuery } from "../../../redux/queries/loan-officer"
import { RateFlowDetails } from "../../../types"
import {
  RateflowSettings,
  LOMap,
  PRICING_ENGINE_LABELS,
  ENGINES_WITH_LO_MAP,
  ENGINES_WITH_CUSTOM_FIELDS,
  CustomFieldValue,
  PollyCustomField
} from "../../../types/pricing-engine"
import { GlobalDefaults } from "./global-defaults-editor"
import { pushToast } from "../../../redux/slices/notifications"
import { useAppDispatch } from "../../hooks"
import { useIsSuperAdmin } from "../../hooks/use-user-roles"
import RateflowSelector from "./rateflow-selector"
import AdminRateflowBrowser from "./admin-rateflow-browser"
import Tabs from "../tabs"
import LOMapManager from "./lo-map-manager/index"
import OBLOMapManager from "./lo-map-manager/ob-lo-map-manager"
import CustomFieldsManager from "./custom-fields-manager"
import PollyCustomFieldsManager from "./polly-custom-fields-manager"
import GeneralSettingsManager from "./general-settings"
import ConnectivityTest from "./connectivity-test"
import { PrimaryBtn, LightBtn } from "../buttons"
import { ListSkeleton } from "../skeletons"
import { Save, RotateCcw } from "lucide-react"
import { PricingEngineType } from "../../../types/pricing-engine"

// Pricing engine logos
import obLogo from "../../../assets/images/ob-logo.png"
import pollyLogo from "../../../assets/images/polly-logo.png"
import loanSifterLogo from "../../../assets/images/loan-sifter-logo.webp"
import lenderPriceLogo from "../../../assets/images/lenderprice-logo.jpg"
import mortechLogo from "../../../assets/images/mortech-logo.png"

const PRICING_ENGINE_LOGOS: Partial<Record<PricingEngineType, string>> = {
  optimal_blue: obLogo,
  polly: pollyLogo,
  loan_sifter: loanSifterLogo,
  lender_price: lenderPriceLogo,
  mortech: mortechLogo
}

type TabId = "general" | "lo-sync" | "custom-fields" | "test"

interface GeneralSettings {
  cache_ttl?: number
  target_price?: number
  prepaid_interest_days?: number
  default_fees_in?: boolean
  fee_engine?: string
  waive_escrows?: boolean
  disable?: boolean
}

const PricingEngineContainer: FC = () => {
  const dispatch = useAppDispatch()
  const userDetails = useAppSelector(selectUserDetails)
  const orgId = userDetails?.org?.id || ""
  const isSuperAdmin = useIsSuperAdmin()

  const [selectedRateflowId, setSelectedRateflowId] = useState<string>("")
  const [activeTab, setActiveTab] = useState<TabId>("general")

  // Local state for pending changes
  const [pendingLoMap, setPendingLoMap] = useState<LOMap | null>(null)
  // OB custom fields (raw_defaults.loanInformation.customFields)
  const [pendingOBCustomFields, setPendingOBCustomFields] = useState<
    CustomFieldValue[] | null
  >(null)
  // Polly custom fields (api_config.custom_values)
  const [pendingPollyCustomFields, setPendingPollyCustomFields] = useState<
    PollyCustomField[] | null
  >(null)
  // General settings (cache_ttl, target_price)
  const [pendingGeneralSettings, setPendingGeneralSettings] =
    useState<GeneralSettings | null>(null)
  // Global defaults (defaults and raw_defaults at rateflow level)
  const [pendingGlobalDefaults, setPendingGlobalDefaults] =
    useState<GlobalDefaults | null>(null)

  // For admins, fetch all rateflows site-wide; for regular users, fetch by org
  const { data: rateflows, isFetching: isFetchingRateflows } =
    useFindRateflowsQuery(isSuperAdmin ? { limit: 999 } : { org_id: orgId }, {
      skip: !isSuperAdmin && !orgId
    })

  const [updateRateflow, { isLoading: isUpdating }] =
    useUpdateOrCreateRateflowMutation()

  // Auto-select when there's only one rateflow (not for admins - they should browse)
  useEffect(() => {
    if (!isSuperAdmin && rateflows?.length === 1 && !selectedRateflowId) {
      setSelectedRateflowId(rateflows[0].id)
    }
  }, [rateflows, selectedRateflowId, isSuperAdmin])

  // Reset pending changes when rateflow changes
  useEffect(() => {
    setPendingLoMap(null)
    setPendingOBCustomFields(null)
    setPendingPollyCustomFields(null)
    setPendingGeneralSettings(null)
    setPendingGlobalDefaults(null)
  }, [selectedRateflowId])

  const selectedRateflow = useMemo(
    () => rateflows?.find(rf => rf.id === selectedRateflowId),
    [rateflows, selectedRateflowId]
  )

  // Load loan officers for the brand that owns the selected rateflow
  const rateflowBrandId = selectedRateflow?.bank_id || ""
  const { data: membersData } = useFetchLoanOfficersQuery(
    { brand_id: rateflowBrandId },
    { skip: !rateflowBrandId }
  )

  const hasMultipleRateflows = (rateflows?.length ?? 0) > 1

  const settings = selectedRateflow?.settings as RateflowSettings | undefined
  const engineType = settings?.api_mode
  const engineLabel = engineType ? PRICING_ENGINE_LABELS[engineType] : ""
  const hasLoMapSupport = engineType && ENGINES_WITH_LO_MAP.includes(engineType)
  const hasCustomFieldsSupport =
    engineType && ENGINES_WITH_CUSTOM_FIELDS.includes(engineType)
  const isPolly = engineType === "polly"
  const isOptimalBlue = engineType === "optimal_blue"

  // Set default tab based on engine capabilities
  useEffect(() => {
    if (!engineType) return

    // If current tab is not supported, switch to a supported one
    if (activeTab === "lo-sync" && !hasLoMapSupport && hasCustomFieldsSupport) {
      setActiveTab("custom-fields")
    } else if (
      activeTab === "custom-fields" &&
      !hasCustomFieldsSupport &&
      hasLoMapSupport
    ) {
      setActiveTab("lo-sync")
    }
  }, [engineType, activeTab, hasLoMapSupport, hasCustomFieldsSupport])

  const members = useMemo(() => membersData?.los || [], [membersData])

  // Current values from server
  const serverLoMap = settings?.lo_map || {}
  const serverOBCustomFields =
    settings?.raw_defaults?.loanInformation?.customFields || []
  const serverPollyCustomFields = settings?.api_config?.custom_values || []
  // General settings (some at root level, some in api_config)
  const serverGeneralSettings: GeneralSettings = {
    cache_ttl: settings?.cache_ttl,
    target_price: settings?.target_price,
    prepaid_interest_days: settings?.api_config?.prepaid_interest_days,
    default_fees_in: settings?.api_config?.default_fees_in,
    fee_engine: settings?.api_config?.fee_engine,
    waive_escrows: settings?.waive_escrows,
    disable: settings?.disable
  }
  // Global defaults (defaults and raw_defaults at rateflow level)
  const serverGlobalDefaults: GlobalDefaults = {
    defaults: settings?.defaults,
    raw_defaults: settings?.raw_defaults
  }

  // Values to display (pending or server)
  const currentLoMap = pendingLoMap ?? serverLoMap
  const currentOBCustomFields = pendingOBCustomFields ?? serverOBCustomFields
  const currentPollyCustomFields =
    pendingPollyCustomFields ?? serverPollyCustomFields
  const currentGeneralSettings = pendingGeneralSettings ?? serverGeneralSettings
  const currentGlobalDefaults = pendingGlobalDefaults ?? serverGlobalDefaults

  // Check if there are unsaved changes
  const hasLoMapChanges = pendingLoMap !== null
  const hasOBCustomFieldChanges = pendingOBCustomFields !== null
  const hasPollyCustomFieldChanges = pendingPollyCustomFields !== null
  const hasGeneralSettingsChanges = pendingGeneralSettings !== null
  const hasGlobalDefaultsChanges = pendingGlobalDefaults !== null
  const hasUnsavedChanges =
    hasLoMapChanges ||
    hasOBCustomFieldChanges ||
    hasPollyCustomFieldChanges ||
    hasGeneralSettingsChanges ||
    hasGlobalDefaultsChanges

  // Count for display
  const customFieldsCount = isPolly
    ? currentPollyCustomFields.length
    : currentOBCustomFields.length

  const tabs = [
    { id: "general" as TabId, label: "General" },
    { id: "lo-sync" as TabId, label: "LO Map" },
    { id: "custom-fields" as TabId, label: "Custom Fields" },
    { id: "test" as TabId, label: "Test" }
  ]

  const handleLoMapChange = useCallback((newLoMap: LOMap) => {
    setPendingLoMap(newLoMap)
  }, [])

  const handleOBCustomFieldsChange = useCallback(
    (newCustomFields: CustomFieldValue[]) => {
      setPendingOBCustomFields(newCustomFields)
    },
    []
  )

  const handlePollyCustomFieldsChange = useCallback(
    (newCustomFields: PollyCustomField[]) => {
      setPendingPollyCustomFields(newCustomFields)
    },
    []
  )

  const handleGeneralSettingsChange = useCallback(
    (newSettings: GeneralSettings) => {
      setPendingGeneralSettings(newSettings)
    },
    []
  )

  const handleGlobalDefaultsChange = useCallback(
    (newDefaults: GlobalDefaults) => {
      setPendingGlobalDefaults(newDefaults)
    },
    []
  )

  const handleDiscardChanges = useCallback(() => {
    setPendingLoMap(null)
    setPendingOBCustomFields(null)
    setPendingPollyCustomFields(null)
    setPendingGeneralSettings(null)
    setPendingGlobalDefaults(null)
  }, [])

  const handleSaveChanges = useCallback(async () => {
    if (!selectedRateflow) {
      console.error("No selected rateflow")
      return
    }

    const updatedSettings = { ...(selectedRateflow.settings || {}) }

    if (pendingLoMap !== null) {
      updatedSettings.lo_map = pendingLoMap
    }

    // OB custom fields
    if (pendingOBCustomFields !== null) {
      updatedSettings.raw_defaults = {
        ...updatedSettings.raw_defaults,
        loanInformation: {
          ...updatedSettings.raw_defaults?.loanInformation,
          customFields: pendingOBCustomFields
        }
      }
    }

    // Polly custom fields (stored in api_config.custom_values)
    if (pendingPollyCustomFields !== null) {
      updatedSettings.api_config = {
        ...updatedSettings.api_config,
        custom_values: pendingPollyCustomFields
      }
    }

    // General settings (some at root level, some in api_config)
    if (pendingGeneralSettings !== null) {
      updatedSettings.cache_ttl = pendingGeneralSettings.cache_ttl
      updatedSettings.target_price = pendingGeneralSettings.target_price
      updatedSettings.waive_escrows = pendingGeneralSettings.waive_escrows
      updatedSettings.disable = pendingGeneralSettings.disable
      // prepaid_interest_days, default_fees_in, fee_engine go in api_config
      updatedSettings.api_config = {
        ...updatedSettings.api_config,
        prepaid_interest_days: pendingGeneralSettings.prepaid_interest_days,
        default_fees_in: pendingGeneralSettings.default_fees_in,
        fee_engine: pendingGeneralSettings.fee_engine
      }
    }

    // Global defaults (defaults and raw_defaults at rateflow level)
    if (pendingGlobalDefaults !== null) {
      updatedSettings.defaults = pendingGlobalDefaults.defaults
      // Merge raw_defaults carefully to preserve loanInformation from OB custom fields
      updatedSettings.raw_defaults = {
        ...updatedSettings.raw_defaults,
        ...pendingGlobalDefaults.raw_defaults
      }
    }

    const payload: Partial<RateFlowDetails> = {
      ...selectedRateflow,
      settings: updatedSettings
    }

    try {
      const result = await updateRateflow(payload).unwrap()

      // Check for API-level error response
      if (result?.status === "error") {
        console.error("API returned error status:", result)
        dispatch(
          pushToast({
            msg: result?.message || "Failed to save settings",
            theme: "danger",
            timeout: 3000
          })
        )
        return
      }

      setPendingLoMap(null)
      setPendingOBCustomFields(null)
      setPendingPollyCustomFields(null)
      setPendingGeneralSettings(null)
      setPendingGlobalDefaults(null)
      dispatch(
        pushToast({
          msg: "Settings saved successfully",
          theme: "success",
          timeout: 3000
        })
      )
    } catch (error) {
      console.error("Save error:", error)
      dispatch(
        pushToast({
          msg: "Failed to save settings",
          theme: "danger",
          timeout: 3000
        })
      )
    }
  }, [
    selectedRateflow,
    pendingLoMap,
    pendingOBCustomFields,
    pendingPollyCustomFields,
    pendingGeneralSettings,
    pendingGlobalDefaults,
    updateRateflow,
    dispatch
  ])

  if (!orgId && !isSuperAdmin) {
    return (
      <div className="text-gray-500">
        No organization associated with your account. Please contact support.
      </div>
    )
  }

  if (isFetchingRateflows) {
    return <ListSkeleton />
  }

  if (!rateflows || rateflows.length === 0) {
    return (
      <div className="text-gray-500">
        No pricing engine configurations found for your organization. Please
        contact support to set up a pricing engine.
      </div>
    )
  }

  // Show admin browser for superadmins when no rateflow is selected
  if (isSuperAdmin && !selectedRateflowId) {
    return (
      <AdminRateflowBrowser
        rateflows={rateflows}
        isLoading={isFetchingRateflows}
        selectedRateflowId={selectedRateflowId}
        onSelectRateflow={setSelectedRateflowId}
      />
    )
  }

  return (
    <div>
      {/* Header with rateflow selector and save button */}
      <div className="flex items-center justify-between mb-6 sticky top-0 z-10 -mx-4 px-4 py-2 bg-white/95 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          {isSuperAdmin ? (
            <button
              onClick={() => setSelectedRateflowId("")}
              className="text-sm text-violet-600 hover:text-violet-700 font-medium"
            >
              &larr; Back to all rateflows
            </button>
          ) : (
            hasMultipleRateflows && (
              <RateflowSelector
                rateflows={rateflows}
                selectedId={selectedRateflowId}
                onChange={setSelectedRateflowId}
              />
            )
          )}
        </div>

        {hasUnsavedChanges && (
          <div className="flex items-center gap-3 bg-white border border-neutral-200 shadow-sm rounded-lg px-4 py-2">
            <span className="text-sm text-amber-600 font-medium">
              Unsaved changes
            </span>
            <LightBtn
              type="button"
              onClick={handleDiscardChanges}
              disabled={isUpdating}
              className="flex items-center"
            >
              <RotateCcw size={16} className="mr-2" />
              Discard
            </LightBtn>
            <PrimaryBtn
              type="button"
              onClick={handleSaveChanges}
              disabled={isUpdating}
              className="flex items-center"
            >
              <Save size={16} className="mr-2" />
              {isUpdating ? "Saving..." : "Save Changes"}
            </PrimaryBtn>
          </div>
        )}
      </div>

      {/* Rateflow info card */}
      {selectedRateflow && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-4 mb-4">
            {engineType && PRICING_ENGINE_LOGOS[engineType] && (
              <img
                src={PRICING_ENGINE_LOGOS[engineType]}
                alt={engineLabel}
                className="h-8 max-w-[140px] w-auto object-contain"
              />
            )}
            <div>
              <h3 className="text-lg font-medium">{selectedRateflow.name}</h3>
              <span className="text-sm text-gray-500">
                {engineLabel || "Unknown"}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Default:</span>{" "}
              <span className="font-medium">
                {selectedRateflow.default === "1" ? "Yes" : "No"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">LO Mappings:</span>{" "}
              <span className="font-medium">
                {Object.keys(currentLoMap).length} configured
              </span>
            </div>
            <div>
              <span className="text-gray-500">Custom Fields:</span>{" "}
              <span className="font-medium">
                {customFieldsCount} configured
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Configuration tabs */}
      {selectedRateflow && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="border-b border-gray-200 px-4">
            <Tabs
              tabs={tabs.filter(tab => {
                if (tab.id === "general") return true // Always show general
                if (tab.id === "lo-sync") return hasLoMapSupport
                if (tab.id === "custom-fields") return hasCustomFieldsSupport
                if (tab.id === "test") return true // Always show test
                return true
              })}
              currentTab={activeTab}
              onChange={setActiveTab}
            />
          </div>

          <div className="p-4">
            {activeTab === "general" && (
              <GeneralSettingsManager
                currentSettings={currentGeneralSettings}
                onChange={handleGeneralSettingsChange}
                currentGlobalDefaults={currentGlobalDefaults}
                onGlobalDefaultsChange={handleGlobalDefaultsChange}
              />
            )}

            {activeTab === "lo-sync" && hasLoMapSupport && (
              <>
                {isOptimalBlue ? (
                  <OBLOMapManager
                    rateflowId={selectedRateflow.id}
                    brandId={rateflowBrandId}
                    currentLoMap={currentLoMap}
                    members={members}
                    onChange={handleLoMapChange}
                    engineType={engineType}
                  />
                ) : (
                  <LOMapManager
                    currentLoMap={currentLoMap}
                    members={members}
                    onChange={handleLoMapChange}
                  />
                )}
              </>
            )}

            {activeTab === "custom-fields" && hasCustomFieldsSupport && (
              <>
                {isOptimalBlue && (
                  <CustomFieldsManager
                    rateflowId={selectedRateflow.id}
                    currentCustomFields={currentOBCustomFields}
                    onChange={handleOBCustomFieldsChange}
                  />
                )}
                {isPolly && (
                  <PollyCustomFieldsManager
                    currentCustomFields={currentPollyCustomFields}
                    onChange={handlePollyCustomFieldsChange}
                  />
                )}
              </>
            )}

            {activeTab === "test" && (
              <ConnectivityTest
                rateflowId={selectedRateflow.id}
                members={members}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PricingEngineContainer
